import { Link } from 'react-router-dom';

interface OperationalGuideProps {
  /** Path to the illustration PNG */
  illustration: string;
  /** Primary heading text */
  heading: string;
  /** Secondary subtext / instruction */
  subtext: string;
  /** CTA button/link label */
  ctaLabel?: string;
  /** When provided, the CTA renders as a <Link> to this path */
  ctaLink?: string;
  /** When provided (and no ctaLink), the CTA renders as a <button> */
  onCtaClick?: () => void;
  /**
   * Compact variant — used inside subsections (cards, widget rows).
   * Full variant fills the viewport height for full-page empty states.
   */
  compact?: boolean;
  /** Forwarded to the root element for targeted tests / analytics */
  'data-guide-id'?: string;
}

export function OperationalGuide({
  illustration,
  heading,
  subtext,
  ctaLabel,
  ctaLink,
  onCtaClick,
  compact = false,
  'data-guide-id': guideId,
}: OperationalGuideProps) {
  const containerClass = compact
    ? 'flex flex-col items-center justify-center py-10 px-6 text-center'
    : 'flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] px-6 text-center';

  const imgClass = compact ? 'w-32 h-32' : 'w-[240px] h-[240px]';

  const hasCta = Boolean(ctaLabel && (ctaLink ?? onCtaClick));

  return (
    <div
      className={containerClass}
      data-testid="operational-guide"
      data-compact={compact ? 'true' : 'false'}
      data-layout={compact ? 'compact' : 'standard'}
      {...(guideId ? { 'data-guide-id': guideId } : {})}
    >
      {/* Transparent PNGs render naturally on the dark #1c1a17 surface — no blend mode needed. */}
      <img
        src={illustration}
        alt=""
        aria-hidden="true"
        className={`${imgClass} object-contain mb-5`}
        data-illustration-opacity="none"
        data-illustration-blend="none"
      />
      <p className="font-display text-display-sm text-[var(--color-text-display)] mb-2">
        {heading}
      </p>
      <p className="text-body-sm text-[var(--color-text-muted)] max-w-xs mb-5">
        {subtext}
      </p>
      {hasCta && ctaLabel && (
        ctaLink ? (
          <Link
            to={ctaLink}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-[var(--color-accent)] text-white text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
            data-testid="operational-guide-cta"
          >
            {ctaLabel}
          </Link>
        ) : (
          <button
            type="button"
            onClick={onCtaClick}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-[var(--color-accent)] text-white text-body-sm font-medium hover:bg-[var(--color-accent-hover)] transition-colors"
            data-testid="operational-guide-cta"
          >
            {ctaLabel}
          </button>
        )
      )}
    </div>
  );
}
